import React from "react"
import { Block, Text } from "@convertkit/slate"
import { useIcon } from "../../icon-context"
import Toolbar from "./toolbar"
import Icon from "@kit/design/icon"
import Portal from "@kit/design/portal"
import { VisuallyHidden } from "@kit/design/visually-hidden"

const LabelStyle = ({ label, icon }) => {
  const LabelIcon = icon
  return (
    <div style={{ display: "flex", fontSize: "14px", alignItems: "center" }}>
      <span
        style={{
          display: "inline-flex",
          justifyContent: "center",
          width: "24px",
          fontSize: "16px",
          marginRight: "4px"
        }}
      >
        <LabelIcon />
      </span>
      {label}
    </div>
  )
}

const createOption = ({ onSelect, icon, label, ...attributes }) => {
  const Label =
    attributes.labelComponent ||
    function () {
      const OptionIcon = useIcon(icon)

      return <LabelStyle label={label} icon={OptionIcon} />
    }

  return {
    onSelect,
    label: <Label />,
    ...attributes
  }
}

const cleanupBeforeChange = (editor, block) => {
  if (["unordered-list", "ordered-list"].includes(block.type)) {
    editor.moveToRangeOfNode(block).unwrapList()
  } else if (block.type === "blockquote") {
    editor.toggleBlockquote()
  }
}

const createChangeBlockOption = ({ type, icon, label }) => {
  const onSelect = (editor, block) => {
    cleanupBeforeChange(editor, block)
    editor.changeBlock(type)
  }

  return createOption({ onSelect, icon, label, type })
}

const createChangeListOption = ({ type, icon, label }) => {
  const onSelect = (editor, block) => {
    cleanupBeforeChange(editor, block)
    editor.wrapList({ type }).focus()
  }

  return createOption({ onSelect, icon, label, type })
}

const createInsertBelowOption = ({
  onSelect,
  icon,
  label,
  type,
  ...attributes
}) => {
  const onOptionSelect = (editor, block) => {
    const index = editor.value.document.nodes.findIndex(
      node => node.key === block.key
    )

    const node = Block.create({
      type: "paragraph",
      nodes: [Text.create()]
    })

    editor
      .insertNodeByKey(editor.value.document.key, index + 1, node)
      .moveToRangeOfNode(node)

    onSelect(editor, block)
  }

  return createOption({
    onSelect: onOptionSelect,
    icon,
    label,
    type,
    ...attributes
  })
}

const ChangeToParagraph = createChangeBlockOption({
  type: "paragraph",
  icon: "paragraph",
  label: "Paragraph"
})

const ChangeToHeading1 = createChangeBlockOption({
  type: "heading-one",
  icon: "heading",
  label: "Heading 1"
})

const ChangeToHeading2 = createChangeBlockOption({
  type: "heading-two",
  icon: "heading",
  label: "Heading 2"
})

const ChangeToHeading3 = createChangeBlockOption({
  type: "heading-three",
  icon: "heading",
  label: "Heading 3"
})

const ChangeToUnorderedList = createChangeListOption({
  type: "unordered-list",
  icon: "unorderedList",
  label: "Unordered List"
})

const ChangeToOrderedList = createChangeListOption({
  type: "ordered-list",
  icon: "orderedList",
  label: "Numbered List"
})

const ChangeToBlockquote = createOption({
  onSelect: editor => editor.toggleBlockquote(),
  icon: "blockquote",
  label: "Blockquote",
  operation: "blockquote",
  type: "blockquote"
})

const InsertPersonalization = createOption({
  onSelect: () => {},
  icon: "personalization",
  label: "Personalization",
  operation: "insert_personalization",
  type: "personalization"
})

const InsertSnippet = createOption({
  onSelect: () => {},
  icon: "snippet",
  label: "Snippet",
  operation: "insert_snippet",
  type: "snippet"
})

const InsertButtonBelow = createInsertBelowOption({
  onSelect: editor => {
    editor
      .setBlocks({
        type: "button",
        data: editor.props.theme.button
      })
      .insertText("Button")
  },
  icon: "button",
  label: "Button",
  type: "button"
})

const InsertButton = createOption({
  onSelect: editor => {
    editor
      .setBlocks({
        type: "button",
        data: editor.props.theme.button
      })
      .insertText("Button")
  },
  icon: "button",
  label: "Button",
  type: "button"
})

const InsertImage = createOption({
  onSelect: () => {},
  icon: "image",
  label: "Image",
  operation: "insert_image",
  type: "image"
})

const InsertGallery = createOption({
  onSelect: () => {},
  icon: "images",
  label: "Gallery",
  operation: "insert_gallery",
  type: "gallery"
})

const InsertLinkCollection = createOption({
  onSelect: () => {},
  icon: "link",
  label: "Links",
  operation: "insert_link_collection",
  type: "links"
})

const InsertIconCollection = createOption({
  onSelect: () => {},
  icon: "share",
  label: "Icons",
  operation: "insert_icon_collection",
  type: "icons"
})

const InsertFile = createOption({
  onSelect: () => {},
  icon: "file",
  label: "File",
  operation: "insert_file",
  type: "file"
})

const InsertHTMLBlockBelow = createOption({
  onSelect: editor => {
    editor.insertHTML({ html: "" })
  },
  icon: "html",
  label: "HTML Block",
  type: "html"
})

const InsertScriptBelow = createOption({
  onSelect: editor => {
    editor.insertScript({ version: 0 }).focus()
  },
  icon: "script",
  label: "Script",
  type: "script"
})

const InsertDivider = createOption({
  onSelect: editor => {
    editor.insertBlock({
      type: "horizontal-rule"
    })
  },
  icon: "horizontalRule",
  label: "Divider",
  type: "horizontal-rule"
})

const InsertDividerBelow = createInsertBelowOption({
  onSelect: editor => {
    editor.setBlocks("horizontal-rule")
  },
  icon: "horizontalRule",
  label: "Divider",
  type: "horizontal-rule"
})

const InsertInstagramBelow = createInsertBelowOption({
  onSelect: () => null,
  icon: "instagram",
  label: "Instagram Feed",
  type: "instagram",
  operation: "insert_instagram"
})

const InsertNewsletterBelow = createInsertBelowOption({
  onSelect: () => null,
  icon: "newsletter",
  label: "Newsletter Feed",
  type: "newsletter",
  operation: "insert_newsletter"
})

const InsertCountdownTimerBelow = createInsertBelowOption({
  onSelect: () => null,
  icon: "stopwatch",
  label: "Countdown Timer",
  type: "countdown_timer",
  operation: "insert_countdown_timer"
})

const createInsertersFor = array =>
  array.map(item =>
    createInsertBelowOption({
      onSelect: () => null,
      labelComponent: () => <LabelStyle icon={item.icon} label={item.label} />,
      type: item.type,
      operation: item.operation
    })
  )

function BlockToolbar({
  onSelect,
  pluginBlocks = {},
  blocks = [],
  collections = [],
  excludeBlocks = []
}) {
  const filter = block => item => {
    if (
      blocks.length > 0 &&
      typeof item !== "string" &&
      !blocks.includes(item.type)
    )
      return false
    if (block.type === item.type) return false
    return true
  }

  const pluginInsertBlocks = pluginBlocks["insert"] || []
  const pluginChangeBlocks = pluginBlocks["change"] || []
  const pluginInsertBelowBlocks = pluginBlocks["insertBelow"] || []

  const options = block => {
    if (!block?.type) return []

    const blank =
      block.text === "" &&
      (block.type === "paragraph" || block.type.includes("heading"))

    if (blank) {
      return [
        "Insert",
        InsertImage,
        InsertFile,
        InsertButton,
        InsertDivider,
        ...pluginInsertBlocks,
        InsertGallery,
        InsertLinkCollection,
        InsertIconCollection,
        InsertCountdownTimerBelow,
        InsertInstagramBelow,
        InsertNewsletterBelow,
        ...createInsertersFor(collections),
        ...pluginInsertBelowBlocks,
        InsertHTMLBlockBelow,
        InsertScriptBelow,
        InsertPersonalization,
        InsertSnippet,
        ChangeToParagraph,
        ChangeToHeading1,
        ChangeToHeading2,
        ChangeToHeading3,
        ChangeToOrderedList,
        ChangeToUnorderedList,
        ChangeToBlockquote,
        ...pluginChangeBlocks
      ].filter(filter(block))
    } else {
      return [
        !excludeBlocks.includes("Insert") && "Insert",
        InsertPersonalization,
        InsertSnippet,
        InsertFile,
        ...pluginInsertBlocks,
        "Change To",
        ChangeToParagraph,
        ChangeToHeading1,
        ChangeToHeading2,
        ChangeToHeading3,
        ChangeToOrderedList,
        ChangeToUnorderedList,
        ChangeToBlockquote,
        ...pluginChangeBlocks,
        "Insert Below",
        InsertImage,
        InsertGallery,
        InsertLinkCollection,
        InsertIconCollection,
        InsertButtonBelow,
        InsertHTMLBlockBelow,
        InsertScriptBelow,
        InsertDividerBelow,
        InsertCountdownTimerBelow,
        InsertInstagramBelow,
        InsertNewsletterBelow,
        ...createInsertersFor(collections),
        ...pluginInsertBelowBlocks
      ].filter(filter(block))
    }
  }

  const children = () => (
    <div style={{ fontSize: "16px" }}>
      <Icon name="plus" />
      <VisuallyHidden>Open Block Toolbar</VisuallyHidden>
    </div>
  )

  return (
    <Portal>
      <Toolbar onSelect={onSelect} options={options}>
        {children}
      </Toolbar>
    </Portal>
  )
}

export { createOption, createInsertBelowOption }

export default BlockToolbar
